// @ts-nocheck
import querystringify from "querystringify";
import * as React from "react";
import { UserService } from "../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { TitleManager } from "../../shared/utility/title-manager";
import AuthVerifyEmailSvg from "../../svgs/auth-verify-email.svg";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaOtpInput, { type KuikaOtpInputStatus } from "../ui-kit/kuika-otp-input/kuika-otp-input";
import { toastService } from "../ui-kit/kuika-toast/toast-service";
import KuikaH4 from "../ui-kit/typography/H4/H4";
import KuikaSmallMediumText from "../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../ui-kit/typography/Text/Text";
import { AuthLogo } from "./auth-logo";

export interface IChangePasswordState {
  userEmail: string;
  verificationCodeError: string;
  isLoading: boolean;
  resendCooldown: number;
  /** The code typed so far, digits only — one field now, not six cells. */
  verificationCode: string;
  focusKey: number;
}

/** The code is six digits; the field, the guard and the enabled Continue all read it from here. */
const VERIFICATION_CODE_LENGTH: number = 6;

export interface IChangePasswordProps {}

interface IPageQueries {
  username?: string;
}

class ChangePassword extends React.PureComponent<IChangePasswordProps & IWithHistoryProp, IChangePasswordState> {
  resendTimer: NodeJS.Timeout | null = null;

  private codeInputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

  constructor(props: IChangePasswordProps & IWithHistoryProp) {
    super(props);
    let userEmail = "";
    const querySearch = this.props?.navigator?.location?.search;
    if (querySearch) {
      const queryParams = querystringify.parse(querySearch) as IPageQueries;
      if (queryParams.username) {
        userEmail = queryParams.username;
      }
    }

    this.state = {
      userEmail,
      verificationCodeError: "",
      isLoading: false,
      resendCooldown: 0,
      verificationCode: "",
      focusKey: 0
    };
  }

  /**
   * A refused code sends the caret back to the field, as a refused form field does (#12). Done here,
   * off the error turning on, so the verification handler itself is left exactly as it was.
   */
  componentDidUpdate(_prevProps: IChangePasswordProps & IWithHistoryProp, prevState: IChangePasswordState): void {
    if (this.state.verificationCodeError && !prevState.verificationCodeError) {
      this.codeInputRef.current?.focus();
    }
  }

  componentDidMount() {
    TitleManager.setPageTitle("Verify Your Email | Kuika", "Main Low Code Verify Email");
    TitleManager.setPageMeta(
      "Verify your email address to activate your Kuika account and start using the low-code platform."
    );
  }

  componentWillUnmount() {
    TitleManager.clearPageTitle();
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  /**
   * Every edit clears the wrong-code message, exactly as the six cells did — and that clearing is
   * also what lets the field shake again on the next failed attempt (`status` has to leave `error`
   * before it can turn back on). The code itself is left alone after a failure, so the user can
   * correct one digit rather than retype all six.
   */
  handleOtpChange = (verificationCode: string): void => {
    this.setState({ verificationCode, verificationCodeError: "" });
  };

  reSendVerifyCode = async () => {
    if (this.state.isLoading || this.state.resendCooldown > 0) return;

    this.setState({ isLoading: true });

    const res = await UserService.ForgotPassword({ Email: this.state.userEmail });

    if (res && res.status === 200) {
      KMainFunctions.displaySuccessNotification("New verification code has been sent to your email");
      // Clear OTP inputs, focus first input, and start 60 second cooldown
      this.setState(
        (prevState) => ({
          verificationCode: "",
          verificationCodeError: "",
          resendCooldown: 60,
          isLoading: false,
          focusKey: prevState.focusKey + 1
        }),
        () => {
          this.resendTimer = setInterval(() => {
            this.setState(
              (prevState) => ({ resendCooldown: prevState.resendCooldown - 1 }),
              () => {
                if (this.state.resendCooldown <= 0) {
                  if (this.resendTimer) clearInterval(this.resendTimer);
                }
              }
            );
          }, 1000);
        }
      );
    } else {
      KMainFunctions.exceptionHandler(res);
      this.setState({ isLoading: false });
    }
  };

  goToSignIn = () => {
    this.props.navigator.gotoLogin();
  };

  goToResetPassword = (user: string, token: string) => {
    this.props.navigator.goToResetPassword(user, token);
  };

  onSubmit = async () => {
    const verificationCode = this.state.verificationCode;

    if (verificationCode.length !== VERIFICATION_CODE_LENGTH) {
      this.setState({ verificationCodeError: "Please enter the 6-digit verification code." });
      return;
    }

    const data = {
      username: this.state.userEmail,
      verificationCode
    };

    this.setState({ isLoading: true, verificationCodeError: "" });
    try {
      UserService.VerifyVerificationCode(data).then(
        (res) => {
          if (res && res.status === 200) {
            this.goToResetPassword(data.username, data.verificationCode);
            return;
          }
          const errorMessage = (res as any).response?.data?.message;
          if (errorMessage === "Verification code is not correct.") {
            this.setState({ isLoading: false, verificationCodeError: "Verification code is not correct." });
          } else {
            this.setState({ isLoading: false });
          }
        },
        (error) => {
          const errorMessage = error.response?.data?.message;
          if (errorMessage === "Verification code is not correct.") {
            this.setState({
              verificationCodeError: "Verification code is not correct.",
              isLoading: false
            });
          } else if (errorMessage) {
            toastService.error(errorMessage);
            this.setState({ isLoading: false });
          }
        }
      );
    } catch (error) {
      this.setState({ isLoading: false });
      KMainFunctions.exceptionHandler(error);
    }
  };

  render() {
    const isMainAI = KMainFunctions.isMainAI();
    const { resendCooldown, verificationCode, verificationCodeError } = this.state;
    const otpStatus: KuikaOtpInputStatus = verificationCodeError ? "error" : "default";

    return (
      <div className="email-confirmation">
        <div className="email-confirmation__content">
          <div className="email-confirmation__top-section">
            <AuthLogo />

            <div className="email-confirmation__check-inbox-card">
              <div className="email-confirmation__title-row">
                <AuthVerifyEmailSvg />
                <KuikaH4>Verify email</KuikaH4>
              </div>

              <KuikaText className="email-confirmation__message-text">
                We've sent a 6-digit verification code to your email. Please enter it below to reset your password.
              </KuikaText>

              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
                {/* Verification code */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {/*
                    One field, six cells. `focusKey` remounts it after a resend, which is how the
                    caret went back to the first cell before — the cleared code alone would not
                    move it. Submitting is still the Continue button's job: the code completing
                    (typed or pasted) does not send it.
                  */}
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <KuikaOtpInput
                      ref={this.codeInputRef}
                      key={`verification-code-${this.state.focusKey}`}
                      value={verificationCode}
                      onChange={this.handleOtpChange}
                      length={VERIFICATION_CODE_LENGTH}
                      status={otpStatus}
                      autoFocus
                      ariaLabel="Verification code"
                      dataTestId="verificationCodeInput"
                    />
                  </div>
                  <div style={{ minHeight: "20px", textAlign: "center" }}>
                    {verificationCodeError && (
                      <KuikaSmallMediumText style={{ color: "var(--red-5)" }}>
                        {verificationCodeError}
                      </KuikaSmallMediumText>
                    )}
                  </div>
                </div>

                <div className="email-confirmation__return-btn">
                  <KuikaButton
                    htmlType="button"
                    size="lg"
                    loading={this.state.isLoading}
                    disabled={verificationCode.length !== VERIFICATION_CODE_LENGTH}
                    style={{ width: "100%" }}
                    uppercase
                    onClick={this.onSubmit}
                  >
                    CONTINUE
                  </KuikaButton>
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <KuikaButton
                    type="tertiary"
                    size="lg"
                    onClick={this.reSendVerifyCode}
                    disabled={resendCooldown > 0}
                    style={{ width: "100%" }}
                  >
                    {resendCooldown > 0 ? `RESEND VERIFICATION CODE (${resendCooldown}s)` : "RESEND VERIFICATION CODE"}
                  </KuikaButton>
                </div>
              </div>
            </div>
          </div>

          {!isMainAI && (
            <div className="email-confirmation__bottom-section">
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/privacy", "_blank")}
              >
                Privacy
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/terms", "_blank")}
              >
                Terms
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const changepass = withHistory<IChangePasswordProps>(ChangePassword);
export { changepass as ChangePassword };
